// JS-side fallback for offline API requests. Mirrors what the service worker
// does, but runs on the main thread so it works regardless of SW controller
// state, deploy timing, or scope mismatches. The SW remains the primary
// interceptor for true network-offline (`navigator.onLine === false`); this
// adapter handles the explicit "Go offline" mode and acts as a safety net.

import { idbService, offlineModeService } from 'utils/services';

// Routes we're willing to serve from IDB while offline.
const ROUTES = [
  // Match order matters — narrow first.
  { method: 'POST', re: /\/visits(\/|$|\?)/i, handler: 'createVisit' },
  { method: 'GET', re: /\/profile(\/|$|\?)/i, handler: 'profile' },
  { method: 'GET', re: /\/assignments(\/|$|\?)/i, handler: 'assignments' },
  { method: 'GET', re: /\/retailers\/[^/?]+/i, handler: 'retailerDetail' },
  { method: 'GET', re: /\/forms\/\d+/i, handler: 'formDetail' },
  // Endpoints we explicitly want to silence in offline mode (no useful local
  // data, but they error noisily otherwise).
  { method: 'GET', re: /\/user-notifications\//i, handler: 'emptyList' },
];

const okResponse = (config, data, status = 200) => ({
  data,
  status,
  statusText: 'OK',
  headers: { 'content-type': 'application/vnd.api+json', sw: 'offline-adapter' },
  config,
  request: null,
});

const matchRoute = (config) => {
  const method = (config.method || 'get').toUpperCase();
  const url = config.url || '';
  return ROUTES.find((r) => r.method === method && r.re.test(url));
};

const handlers = {
  profile: async () => {
    const db = await idbService.initiateIDB();
    const data = await db.transaction('profile').objectStore('profile').get('profile');
    return data || { data: null, included: [] };
  },

  assignments: async (config) => {
    const db = await idbService.initiateIDB();
    const staged = await db.transaction('offline_assignments').objectStore('offline_assignments').getAll();
    const offlineSurveys = await db.transaction('surveys').objectStore('surveys').getAll();
    const surveysAssignmentIds = offlineSurveys.map((s) => s?.data?.relationships?.assignment?.data?.id);

    const url = new URL((config.baseURL || '') + config.url, window.location.origin);
    const page = parseInt(url.searchParams.get('page'), 10) || 1;
    const pageSize = parseInt(url.searchParams.get('page_size'), 10) || 100;

    const all = staged.filter((a) => !surveysAssignmentIds.includes(a.id)).map(({ _display, ...rest }) => rest);
    const count = all.length;
    const start = (page - 1) * pageSize;
    return {
      data: all.slice(start, start + pageSize),
      included: [],
      meta: { pagination: { count, page, pages: Math.max(1, Math.ceil(count / pageSize)) } },
    };
  },

  retailerDetail: async (config) => {
    const id = config.url.split('?')[0].split('/').pop();
    const db = await idbService.initiateIDB();
    const data = await db.transaction('retailers').objectStore('retailers').get(id);
    if (!data) {
      const e = new Error(`Retailer ${id} not available offline`);
      e.response = { status: 404, data: { errors: [{ detail: e.message }] } };
      throw e;
    }
    return data;
  },

  formDetail: async (config) => {
    // URL shape: /forms/123?include=agency&retailer=...&assignment=...
    const idMatch = config.url.match(/\/forms\/(\d+)/);
    const id = idMatch ? idMatch[1] : null;
    const url = new URL((config.baseURL || '') + config.url, window.location.origin);
    const assignmentId = url.searchParams.get('assignment');

    const db = await idbService.initiateIDB();
    const formData = await db.transaction('forms').objectStore('forms').get(id);
    if (!formData) {
      const e = new Error(`Form ${id} not available offline`);
      e.response = { status: 404, data: { errors: [{ detail: e.message }] } };
      throw e;
    }

    // Enrich youth_id question with per-assignment options (mirrors SW logic).
    const assignment = assignmentId
      ? await db.transaction('offline_assignments').objectStore('offline_assignments').get(assignmentId)
      : null;
    const youthList = assignment?.attributes?.youth;
    if (youthList && formData?.data?.attributes?.form_json?.pages) {
      const youthIDFormData = youthList.reduce(
        (acc, item) => {
          acc.birthdate = { ...acc.birthdate, [item.youth_id]: item.birthdate };
          acc.choices.push({ text: item.youth_id, value: item.youth_id });
          acc.gender = { ...acc.gender, [item.youth_id]: item.gender };
          return acc;
        },
        { birthdate: {}, choices: [], gender: {} },
      );
      const oldPages = formData.data.attributes.form_json.pages;
      const updatedPages = oldPages.map((page) => {
        // eslint-disable-next-line no-prototype-builtins
        const isQuestions = page.hasOwnProperty('questions');
        const questions = page[isQuestions ? 'questions' : 'elements'].map((q) =>
          q.name === 'youth_id'
            ? {
                ...q,
                birthdate: youthIDFormData.birthdate,
                choices: youthIDFormData.choices,
                gender: youthIDFormData.gender,
              }
            : q,
        );
        return { ...page, questions };
      });
      // Avoid mutating the cached form payload.
      return {
        ...formData,
        data: {
          ...formData.data,
          attributes: {
            ...formData.data.attributes,
            form_json: { ...formData.data.attributes.form_json, pages: updatedPages },
          },
        },
      };
    }
    return formData;
  },

  createVisit: async (config) => {
    // config.data is a JSON string when sent through axios; allow object too.
    const body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
    const visitData = body?.data;
    if (!visitData) {
      const e = new Error('No visit body to save');
      e.response = { status: 400, data: { errors: [{ detail: e.message }] } };
      throw e;
    }
    const visitTypes = { 1: 'Assessment', 2: 'Enforcement', 3: 'Education' };
    const retailerId = visitData.relationships?.retailer?.data?.id;
    const db = await idbService.initiateIDB();
    const retailerData = retailerId ? await db.transaction('retailers').objectStore('retailers').get(retailerId) : null;
    const retailerName = retailerData?.data?.attributes?.name || 'Retailer';
    const visitKey = `${
      visitTypes[visitData.attributes?.visit_type] || 'Visit'
    } visit conducted @ ${retailerName} [${retailerId}]`;
    await db.transaction('surveys', 'readwrite').objectStore('surveys').put({ data: visitData }, visitKey);
    return { data: visitData };
  },

  emptyList: async () => ({ data: [], included: [], meta: { pagination: { count: 0 } } }),
};

// Returns true when we should serve from IDB instead of the network.
export const shouldUseOfflineAdapter = () =>
  offlineModeService.getCached() === true || (typeof navigator !== 'undefined' && navigator.onLine === false);

// Axios adapter: short-circuits the request and resolves with synthesized
// data from IDB. Falls through to the next adapter chain (provided as
// `nextAdapter`) for unmatched routes — typically to fail fast with a clear
// "this endpoint isn't available offline" error rather than a DNS failure.
export const createOfflineAdapter = (nextAdapter) => async (config) => {
  const route = matchRoute(config);
  if (!route) {
    if (typeof nextAdapter === 'function') return nextAdapter(config);
    const e = new Error(`Endpoint ${config.url} is not available offline`);
    e.response = { status: 503, data: { errors: [{ detail: e.message }] } };
    throw e;
  }
  const data = await handlers[route.handler](config);
  return okResponse(config, data, route.handler === 'createVisit' ? 201 : 200);
};
